import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { format } from 'date-fns';
import type {
  Court,
  PlayEvent,
  PlayEventUpdateInput,
  PlayJoinPolicy,
  PlayPaymentMode,
  PlayGenderRestriction,
} from '@court-sync/shared';
import {
  ODD_PLAYER_POLICIES,
  PLAY_PAYMENT_MODES,
  PLAY_GENDER_RESTRICTIONS,
  addMinutesToDate,
  computeSocialEventDurationMinutes,
  formatPlayEventDuration,
  participantTypesForJoinPolicy,
  playJoinPoliciesForSelect,
  pricingParticipantTypesForJoinPolicy,
} from '@court-sync/shared';
import toast from 'react-hot-toast';
import { DashboardDataCard } from '@/components/dashboard/DashboardSectionCard';
import {
  Badge,
  Button,
  Checkbox,
  Field,
  FieldDescription,
  FieldLabel,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui';
import { SelectableCard, SelectableCardGrid } from '@/components/onboarding/SelectableCard';
import { LessonCoverImageField } from '@/components/lessons/LessonCoverImageField';
import { useCoverImageUpload } from '@/hooks/useCoverImageUpload';
import { uploadPlayEventImage } from '@/services/storage';
import {
  ODD_PLAYER_POLICY_LABELS,
  PLAY_EVENT_FORMAT_LABELS,
  PLAY_EVENT_SIDE_LABELS,
  PLAY_JOIN_POLICY_LABELS,
  PLAY_GENDER_RESTRICTION_LABELS,
  PLAY_PAYMENT_MODE_LABELS,
} from '@/lib/playEventLabels';
import { MapPin, Star } from 'lucide-react';
import { cn } from '@/lib/utils';
import { clubService } from '@/services/club';
import { playEventService } from '@/services/playEvent';
import { useClubModuleAccess } from '@/hooks/useClubModuleAccess';

const SETTINGS_FORM_ID = 'play-event-settings-form';

function toDatetimeLocalValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function parseDatetimeLocal(value: string): Date {
  return new Date(value);
}

function durationPartsFromRange(startsAt: Date, endsAt: Date): { hours: string; minutes: string } {
  const totalMinutes = Math.max(0, Math.round((endsAt.getTime() - startsAt.getTime()) / 60000));
  return {
    hours: String(Math.floor(totalMinutes / 60)),
    minutes: String(totalMinutes % 60),
  };
}

function formatPriceCents(cents: number | null | undefined): string {
  if (cents == null) return 'Free';
  return `$${(cents / 100).toFixed(2)}`;
}

function parsePriceToCents(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return Math.round(parsed * 100);
}

function SettingItem({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="rounded-lg border border-border bg-muted/20 px-4 py-3">
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="mt-1 text-sm font-medium text-foreground">{value}</dd>
    </div>
  );
}

function SettingsSection({
  title,
  description,
  children,
  compact = false,
}: {
  title: string;
  description?: string;
  children: ReactNode;
  compact?: boolean;
}) {
  return (
    <section
      className={cn(
        'border-b border-border last:border-b-0',
        compact ? 'pb-6 last:pb-0' : 'px-4 py-5 sm:px-6'
      )}
    >
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      {description ? <p className="mt-1 text-sm text-muted-foreground">{description}</p> : null}
      <div className="mt-4">{children}</div>
    </section>
  );
}

interface PlayEventSettingsPanelProps {
  organizationId: string;
  clubId: string;
  event: PlayEvent;
  onUpdated: () => void | Promise<void>;
}

export function PlayEventSettingsPanel({
  organizationId,
  clubId,
  event,
  onUpdated,
}: PlayEventSettingsPanelProps) {
  const { hasClubManagement, hasAcademyManagement } = useClubModuleAccess();
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingCourts, setLoadingCourts] = useState(false);
  const [courts, setCourts] = useState<Court[]>([]);

  const startsAtDate = event.startsAt instanceof Date ? event.startsAt : new Date(event.startsAt);
  const endsAtDate = event.endsAt instanceof Date ? event.endsAt : new Date(event.endsAt);
  const initialDuration = durationPartsFromRange(startsAtDate, endsAtDate);

  const [joinPolicy, setJoinPolicy] = useState<PlayJoinPolicy>(event.joinPolicy);
  const [paymentMode, setPaymentMode] = useState<PlayPaymentMode>(event.paymentMode);
  const [genderRestriction, setGenderRestriction] = useState<PlayGenderRestriction>(
    event.genderRestriction ?? 'open'
  );
  const [capacity, setCapacity] = useState(event.capacity != null ? String(event.capacity) : '');
  const [matchupVisibilityMinutes, setMatchupVisibilityMinutes] = useState(
    String(event.matchupVisibilityMinutes)
  );
  const [nextMatchWithdrawMinutes, setNextMatchWithdrawMinutes] = useState(
    String(event.nextMatchWithdrawMinutes)
  );
  const [roundCount, setRoundCount] = useState(
    event.roundCount != null ? String(event.roundCount) : '4'
  );
  const [roundDurationMinutes, setRoundDurationMinutes] = useState(
    event.roundDurationMinutes != null ? String(event.roundDurationMinutes) : '30'
  );
  const [roundBreakMinutes, setRoundBreakMinutes] = useState(
    event.roundBreakMinutes != null ? String(event.roundBreakMinutes) : '0'
  );
  const [oddPlayerPolicy, setOddPlayerPolicy] = useState(event.oddPlayerPolicy ?? 'sit_out');
  const [startsAt, setStartsAt] = useState(toDatetimeLocalValue(startsAtDate));
  const [durationHours, setDurationHours] = useState(initialDuration.hours);
  const [durationMinutes, setDurationMinutes] = useState(initialDuration.minutes);
  const [courtIds, setCourtIds] = useState<string[]>(event.courtIds);
  const [memberPrice, setMemberPrice] = useState(
    event.memberPriceCents != null ? String(event.memberPriceCents / 100) : ''
  );
  const [studentPrice, setStudentPrice] = useState(
    event.studentPriceCents != null ? String(event.studentPriceCents / 100) : ''
  );
  const [guestPrice, setGuestPrice] = useState(
    event.guestPriceCents != null ? String(event.guestPriceCents / 100) : ''
  );
  const [isFeatured, setIsFeatured] = useState(event.isFeatured);
  const [kioskRegistrationEnabled, setKioskRegistrationEnabled] = useState(
    event.kioskRegistrationEnabled
  );
  const [kioskGuestRegistrationEnabled, setKioskGuestRegistrationEnabled] = useState(
    event.kioskGuestRegistrationEnabled
  );
  const [matchScoringEnabled, setMatchScoringEnabled] = useState(event.matchScoringEnabled);
  const [coverImageUrl, setCoverImageUrl] = useState(event.coverImageUrl ?? '');

  const {
    displayUrl: coverDisplayUrl,
    uploading: uploadingCover,
    handleFileSelect: handleCoverFileSelect,
    handleRemove: handleCoverRemove,
    clear: clearCoverUpload,
    resolveImagePayloadForSubmit: resolveCoverImagePayload,
  } = useCoverImageUpload({
    academyId: organizationId,
    entityId: event.id,
    imageUrl: coverImageUrl,
    setImageUrl: setCoverImageUrl,
    upload: uploadPlayEventImage,
  });

  const canEdit = event.status !== 'cancelled' && event.status !== 'completed';
  const canEditSchedule = canEdit && event.status !== 'in_progress';
  const canEditCourts = canEdit;

  const resetForm = () => {
    setJoinPolicy(event.joinPolicy);
    setPaymentMode(event.paymentMode);
    setGenderRestriction(event.genderRestriction ?? 'open');
    setCapacity(event.capacity != null ? String(event.capacity) : '');
    setMatchupVisibilityMinutes(String(event.matchupVisibilityMinutes));
    setNextMatchWithdrawMinutes(String(event.nextMatchWithdrawMinutes));
    setRoundCount(event.roundCount != null ? String(event.roundCount) : '4');
    setRoundDurationMinutes(event.roundDurationMinutes != null ? String(event.roundDurationMinutes) : '30');
    setRoundBreakMinutes(event.roundBreakMinutes != null ? String(event.roundBreakMinutes) : '0');
    setOddPlayerPolicy(event.oddPlayerPolicy ?? 'sit_out');
    setStartsAt(toDatetimeLocalValue(startsAtDate));
    setDurationHours(initialDuration.hours);
    setDurationMinutes(initialDuration.minutes);
    setCourtIds(event.courtIds);
    setMemberPrice(event.memberPriceCents != null ? String(event.memberPriceCents / 100) : '');
    setStudentPrice(event.studentPriceCents != null ? String(event.studentPriceCents / 100) : '');
    setGuestPrice(event.guestPriceCents != null ? String(event.guestPriceCents / 100) : '');
    setIsFeatured(event.isFeatured);
    setKioskRegistrationEnabled(event.kioskRegistrationEnabled);
    setKioskGuestRegistrationEnabled(event.kioskGuestRegistrationEnabled);
    setMatchScoringEnabled(event.matchScoringEnabled);
    setCoverImageUrl(event.coverImageUrl ?? '');
    clearCoverUpload();
  };

  useEffect(() => {
    let cancelled = false;
    const loadCourts = async () => {
      setLoadingCourts(true);
      try {
        const res = await clubService.getClubCourts(clubId);
        if (!cancelled && res.success && res.data) {
          setCourts(res.data);
        }
      } finally {
        if (!cancelled) setLoadingCourts(false);
      }
    };
    void loadCourts();
    return () => {
      cancelled = true;
    };
  }, [clubId]);

  useEffect(() => {
    resetForm();
    setIsEditing(false);
  }, [
    event.id,
    event.updatedAt,
    event.joinPolicy,
    event.paymentMode,
    event.capacity,
    event.matchupVisibilityMinutes,
    event.nextMatchWithdrawMinutes,
    event.roundCount,
    event.roundDurationMinutes,
    event.roundBreakMinutes,
    event.oddPlayerPolicy,
    event.startsAt,
    event.endsAt,
    event.courtIds,
    event.memberPriceCents,
    event.studentPriceCents,
    event.guestPriceCents,
    event.isFeatured,
    event.kioskRegistrationEnabled,
    event.kioskGuestRegistrationEnabled,
    event.matchScoringEnabled,
    event.coverImageUrl,
  ]);

  const courtNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const court of courts) {
      map.set(court.id, court.name);
    }
    return map;
  }, [courts]);

  const derivedEndLabel = useMemo(() => {
    if (!startsAt) return null;

    if (event.format === 'social_rotate') {
      const rounds = Number(roundCount);
      const roundMin = Number(roundDurationMinutes);
      const breakMin = Number(roundBreakMinutes);
      if (
        !Number.isFinite(rounds) ||
        rounds < 1 ||
        !Number.isFinite(roundMin) ||
        roundMin < 5 ||
        !Number.isFinite(breakMin) ||
        breakMin < 0
      ) {
        return null;
      }
      const totalMinutes = computeSocialEventDurationMinutes(rounds, roundMin, breakMin);
      if (totalMinutes < 1) return null;
      const end = addMinutesToDate(parseDatetimeLocal(startsAt), totalMinutes);
      return {
        durationLabel: formatPlayEventDuration(totalMinutes),
        endLabel: format(end, 'EEE d MMM yyyy · HH:mm'),
      };
    }

    const hours = Number(durationHours);
    const minutes = Number(durationMinutes);
    if (
      !Number.isFinite(hours) ||
      hours < 0 ||
      !Number.isFinite(minutes) ||
      minutes < 0 ||
      minutes > 59
    ) {
      return null;
    }
    const totalMinutes = hours * 60 + minutes;
    if (totalMinutes < 1) return null;
    const end = addMinutesToDate(parseDatetimeLocal(startsAt), totalMinutes);
    return {
      durationLabel: formatPlayEventDuration(totalMinutes),
      endLabel: format(end, 'EEE d MMM yyyy · HH:mm'),
    };
  }, [
    durationHours,
    durationMinutes,
    event.format,
    roundBreakMinutes,
    roundCount,
    roundDurationMinutes,
    startsAt,
  ]);

  const pricingParticipantTypes = useMemo(
    () =>
      pricingParticipantTypesForJoinPolicy(joinPolicy, {
        hasClubManagement,
        hasAcademyManagement,
      }),
    [joinPolicy, hasClubManagement, hasAcademyManagement]
  );

  const availableJoinPolicies = useMemo(
    () =>
      playJoinPoliciesForSelect(
        { hasClubManagement, hasAcademyManagement },
        joinPolicy
      ),
    [hasClubManagement, hasAcademyManagement, joinPolicy]
  );

  const readOnlyPricingParticipantTypes = useMemo(
    () => participantTypesForJoinPolicy(event.joinPolicy),
    [event.joinPolicy]
  );

  const pricingGridCols = (count: number) =>
    count === 1 ? 'sm:grid-cols-1' : count === 2 ? 'sm:grid-cols-2' : 'sm:grid-cols-3';

  const toggleCourt = (courtId: string) => {
    setCourtIds((prev) =>
      prev.includes(courtId) ? prev.filter((id) => id !== courtId) : [...prev, courtId]
    );
  };

  const handleCancelEdit = () => {
    resetForm();
    setIsEditing(false);
  };

  const handleSave = async (formEvent: React.FormEvent) => {
    formEvent.preventDefault();
    if (canEditCourts && courtIds.length === 0) {
      toast.error('Select at least one court');
      return;
    }

    const visibilityMinutes = Number(matchupVisibilityMinutes);
    if (!Number.isFinite(visibilityMinutes) || visibilityMinutes < 0 || visibilityMinutes > 180) {
      toast.error('Matchup visibility must be between 0 and 180 minutes');
      return;
    }

    const withdrawMinutes = Number(nextMatchWithdrawMinutes);
    if (!Number.isFinite(withdrawMinutes) || withdrawMinutes < 0 || withdrawMinutes > 120) {
      toast.error('Next-match withdraw window must be between 0 and 120 minutes');
      return;
    }

    let resolvedCoverImageUrl: string | null | undefined;
    try {
      const coverPayload = resolveCoverImagePayload();
      if (coverPayload.imageUrl !== undefined) {
        resolvedCoverImageUrl = coverPayload.imageUrl;
      } else if (coverImageUrl.trim()) {
        resolvedCoverImageUrl = coverImageUrl.trim();
      }
    } catch {
      toast.error('Please wait for the cover image upload to finish');
      return;
    }

    const payload: PlayEventUpdateInput = {
      joinPolicy,
      paymentMode,
      genderRestriction,
      capacity: capacity.trim() ? Number(capacity) : null,
      matchupVisibilityMinutes: visibilityMinutes,
      nextMatchWithdrawMinutes: withdrawMinutes,
      memberPriceCents: pricingParticipantTypes.includes('member')
        ? parsePriceToCents(memberPrice)
        : null,
      studentPriceCents: pricingParticipantTypes.includes('student')
        ? parsePriceToCents(studentPrice)
        : null,
      guestPriceCents: pricingParticipantTypes.includes('guest')
        ? parsePriceToCents(guestPrice)
        : null,
      isFeatured,
      kioskRegistrationEnabled,
      kioskGuestRegistrationEnabled: kioskRegistrationEnabled
        ? kioskGuestRegistrationEnabled
        : false,
      matchScoringEnabled,
      ...(resolvedCoverImageUrl !== undefined ? { coverImageUrl: resolvedCoverImageUrl } : {}),
    };

    if (canEditCourts) {
      payload.courtIds = courtIds;
    }

    if (canEditSchedule) {
      const startDate = parseDatetimeLocal(startsAt);
      let endDate: Date;

      payload.startsAt = startDate;

      if (event.format === 'social_rotate') {
        const rounds = Number(roundCount);
        const roundMin = Number(roundDurationMinutes);
        const breakMin = Number(roundBreakMinutes);
        if (!Number.isFinite(rounds) || rounds < 1 || rounds > 50) {
          toast.error('Enter a valid number of rounds');
          return;
        }
        if (!Number.isFinite(roundMin) || roundMin < 5 || roundMin > 240) {
          toast.error('Round duration must be between 5 and 240 minutes');
          return;
        }
        if (!Number.isFinite(breakMin) || breakMin < 0 || breakMin > 120) {
          toast.error('Break duration must be between 0 and 120 minutes');
          return;
        }
        const totalMinutes = computeSocialEventDurationMinutes(rounds, roundMin, breakMin);
        endDate = addMinutesToDate(startDate, totalMinutes);
        payload.oddPlayerPolicy = oddPlayerPolicy;
        payload.roundCount = rounds;
        payload.roundDurationMinutes = roundMin;
        payload.roundBreakMinutes = breakMin;
      } else {
        const hours = Number(durationHours);
        const minutes = Number(durationMinutes);
        if (
          !Number.isFinite(hours) ||
          hours < 0 ||
          !Number.isFinite(minutes) ||
          minutes < 0 ||
          minutes > 59
        ) {
          toast.error('Enter a valid duration');
          return;
        }
        const totalMinutes = hours * 60 + minutes;
        if (totalMinutes < 1) {
          toast.error('Duration must be at least 1 minute');
          return;
        }
        endDate = addMinutesToDate(startDate, totalMinutes);
      }

      payload.endsAt = endDate;
    } else if (event.format === 'social_rotate') {
      payload.oddPlayerPolicy = oddPlayerPolicy;
    }

    if (capacity.trim()) {
      const cap = Number(capacity);
      if (!Number.isFinite(cap) || cap < 1) {
        toast.error('Capacity must be at least 1');
        return;
      }
    }

    setSaving(true);
    try {
      const res = await playEventService.updateEvent(organizationId, event.id, payload);
      if (res.success) {
        toast.success('Event settings saved');
        setIsEditing(false);
        await onUpdated();
      } else {
        toast.error(res.error ?? 'Failed to save settings');
      }
    } finally {
      setSaving(false);
    }
  };

  const courtLabels =
    event.courtIds.length === 0
      ? 'None selected'
      : loadingCourts
        ? 'Loading courts…'
        : event.courtIds
            .map((id) => courtNameById.get(id) ?? 'Unknown court')
            .join(', ');

  return (
    <DashboardDataCard
      title="Event settings"
      description={
        isEditing
          ? 'Update registration, schedule, and pricing for this event.'
          : 'Registration rules, schedule, and pricing for this event.'
      }
      headerAction={
        canEdit && !isEditing ? (
          <Button type="button" size="sm" variant="outline" onClick={() => setIsEditing(true)}>
            Edit settings
          </Button>
        ) : undefined
      }
      contentClassName={isEditing ? 'px-4 py-5 sm:px-6' : 'p-0'}
    >
      {isEditing ? (
        <form id={SETTINGS_FORM_ID} className="space-y-6" onSubmit={(e) => void handleSave(e)}>
          <div className="flex flex-wrap gap-2">
            <Badge variant="secondary">{PLAY_EVENT_FORMAT_LABELS[event.format]}</Badge>
            <Badge variant="outline">{PLAY_EVENT_SIDE_LABELS[event.side]}</Badge>
            {isFeatured ? (
              <Badge variant="warning" className="gap-1">
                <Star className="h-3 w-3 fill-current" aria-hidden />
                Featured
              </Badge>
            ) : null}
          </div>

          <SettingsSection
            title="Courts"
            description={
              canEditCourts
                ? 'Courts reserved for the full event window. You can update these while the event is in progress.'
                : 'Courts cannot be changed after the event is completed or cancelled.'
            }
            compact
          >
            <Field>
              {loadingCourts ? (
                <p className="text-sm text-muted-foreground">Loading courts…</p>
              ) : courts.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border bg-muted/20 px-4 py-6 text-center text-sm text-muted-foreground">
                  No courts found. Add courts under Courts &amp; locations first.
                </div>
              ) : (
                <>
                  <SelectableCardGrid label="Select courts" selectionMode="multiple">
                    {courts.map((court) => (
                      <SelectableCard
                        key={court.id}
                        id={`play-event-settings-court-${court.id}`}
                        title={court.name}
                        selected={courtIds.includes(court.id)}
                        onSelect={() => toggleCourt(court.id)}
                        disabled={!canEditCourts}
                        selectionMode="multiple"
                        icon={<MapPin className="h-4 w-4" aria-hidden />}
                      />
                    ))}
                  </SelectableCardGrid>
                  <FieldDescription className="mt-3">
                    Match draws assign courts from this list. Add more courts when you need parallel
                    matches in a round.
                  </FieldDescription>
                </>
              )}
            </Field>
          </SettingsSection>

          <SettingsSection
            title="Schedule"
            description={
              canEditSchedule
                ? 'When the event runs.'
                : 'Start time and duration are locked while the event is in progress.'
            }
            compact
          >
            <div className="space-y-4">
              <Field>
                <FieldLabel htmlFor="play-event-settings-starts">Starts</FieldLabel>
                <Input
                  id="play-event-settings-starts"
                  type="datetime-local"
                  value={startsAt}
                  onChange={(e) => setStartsAt(e.target.value)}
                  disabled={!canEditSchedule}
                  required={canEditSchedule}
                />
              </Field>

              {event.format === 'social_rotate' ? (
                <div className="grid gap-4 sm:grid-cols-3">
                  <Field>
                    <FieldLabel htmlFor="play-event-settings-rounds">Rounds</FieldLabel>
                    <Input
                      id="play-event-settings-rounds"
                      type="number"
                      min={1}
                      max={50}
                      value={roundCount}
                      onChange={(e) => setRoundCount(e.target.value)}
                      disabled={!canEditSchedule}
                      required={canEditSchedule}
                    />
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="play-event-settings-round-duration">
                      Round duration (min)
                    </FieldLabel>
                    <Input
                      id="play-event-settings-round-duration"
                      type="number"
                      min={5}
                      max={240}
                      step={5}
                      value={roundDurationMinutes}
                      onChange={(e) => setRoundDurationMinutes(e.target.value)}
                      disabled={!canEditSchedule}
                      required={canEditSchedule}
                    />
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="play-event-settings-round-break">
                      Break between rounds (min)
                    </FieldLabel>
                    <Input
                      id="play-event-settings-round-break"
                      type="number"
                      min={0}
                      max={120}
                      value={roundBreakMinutes}
                      onChange={(e) => setRoundBreakMinutes(e.target.value)}
                      disabled={!canEditSchedule}
                      required={canEditSchedule}
                    />
                  </Field>
                </div>
              ) : (
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field>
                    <FieldLabel htmlFor="play-event-settings-duration-hours">Duration (hours)</FieldLabel>
                    <Input
                      id="play-event-settings-duration-hours"
                      type="number"
                      min={0}
                      value={durationHours}
                      onChange={(e) => setDurationHours(e.target.value)}
                      disabled={!canEditSchedule}
                      required={canEditSchedule}
                    />
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="play-event-settings-duration-minutes">
                      Duration (minutes)
                    </FieldLabel>
                    <Input
                      id="play-event-settings-duration-minutes"
                      type="number"
                      min={0}
                      max={59}
                      value={durationMinutes}
                      onChange={(e) => setDurationMinutes(e.target.value)}
                      disabled={!canEditSchedule}
                      required={canEditSchedule}
                    />
                  </Field>
                </div>
              )}

              {derivedEndLabel ? (
                <p className="text-sm text-muted-foreground">
                  Event duration {derivedEndLabel.durationLabel} · Ends {derivedEndLabel.endLabel}
                </p>
              ) : null}
            </div>
          </SettingsSection>

          <SettingsSection
            title="Registration"
            description="Who can join and how they pay."
            compact
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <Field>
                <FieldLabel>Join policy</FieldLabel>
                <Select value={joinPolicy} onValueChange={(v) => setJoinPolicy(v as PlayJoinPolicy)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {availableJoinPolicies.map((value) => (
                      <SelectItem key={value} value={value}>
                        {PLAY_JOIN_POLICY_LABELS[value]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field>
                <FieldLabel>Payment</FieldLabel>
                <Select
                  value={paymentMode}
                  onValueChange={(v) => setPaymentMode(v as PlayPaymentMode)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PLAY_PAYMENT_MODES.map((value) => (
                      <SelectItem key={value} value={value}>
                        {PLAY_PAYMENT_MODE_LABELS[value]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field>
                <FieldLabel>Gender restriction</FieldLabel>
                <Select
                  value={genderRestriction}
                  onValueChange={(v) => setGenderRestriction(v as PlayGenderRestriction)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PLAY_GENDER_RESTRICTIONS.map((value) => (
                      <SelectItem key={value} value={value}>
                        {PLAY_GENDER_RESTRICTION_LABELS[value]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field>
                <FieldLabel htmlFor="play-event-settings-capacity">Capacity</FieldLabel>
                <Input
                  id="play-event-settings-capacity"
                  type="number"
                  min={1}
                  value={capacity}
                  onChange={(e) => setCapacity(e.target.value)}
                  placeholder="Unlimited"
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="play-event-settings-visibility">
                  Matchup visibility (minutes before start)
                </FieldLabel>
                <Input
                  id="play-event-settings-visibility"
                  type="number"
                  min={0}
                  max={180}
                  value={matchupVisibilityMinutes}
                  onChange={(e) => setMatchupVisibilityMinutes(e.target.value)}
                  required
                />
              </Field>
              <label className="flex items-start gap-2 text-sm sm:col-span-2">
                <Checkbox
                  className="mt-0.5"
                  checked={matchScoringEnabled}
                  onCheckedChange={(value) => setMatchScoringEnabled(Boolean(value))}
                />
                <span>
                  Record match scores
                  <span className="mt-1 block text-muted-foreground">
                    When enabled, operators can record results and set scores from the match draw.
                  </span>
                </span>
              </label>
            </div>
          </SettingsSection>

          {event.format === 'social_rotate' ? (
            <SettingsSection
              title="Social rotate"
              description="Round behaviour and withdrawal rules."
              compact
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <Field>
                  <FieldLabel>Odd players</FieldLabel>
                  <Select
                    value={oddPlayerPolicy}
                    onValueChange={(v) => setOddPlayerPolicy(v as typeof oddPlayerPolicy)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ODD_PLAYER_POLICIES.map((value) => (
                        <SelectItem key={value} value={value}>
                          {ODD_PLAYER_POLICY_LABELS[value]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field>
                  <FieldLabel htmlFor="play-event-settings-withdraw">
                    Next-match withdraw window (minutes)
                  </FieldLabel>
                  <Input
                    id="play-event-settings-withdraw"
                    type="number"
                    min={0}
                    max={120}
                    value={nextMatchWithdrawMinutes}
                    onChange={(e) => setNextMatchWithdrawMinutes(e.target.value)}
                    required
                  />
                </Field>
              </div>
            </SettingsSection>
          ) : null}

          <SettingsSection
            title="Pricing"
            description="Leave blank for free entry. Amounts are per participant."
            compact
          >
            <div className={cn('grid gap-4', pricingGridCols(pricingParticipantTypes.length))}>
              {pricingParticipantTypes.includes('member') ? (
                <Field>
                  <FieldLabel htmlFor="play-event-settings-member-price">Member price</FieldLabel>
                  <Input
                    id="play-event-settings-member-price"
                    type="number"
                    min={0}
                    step="0.01"
                    value={memberPrice}
                    onChange={(e) => setMemberPrice(e.target.value)}
                    placeholder="Free"
                  />
                </Field>
              ) : null}
              {pricingParticipantTypes.includes('student') ? (
                <Field>
                  <FieldLabel htmlFor="play-event-settings-student-price">Student price</FieldLabel>
                  <Input
                    id="play-event-settings-student-price"
                    type="number"
                    min={0}
                    step="0.01"
                    value={studentPrice}
                    onChange={(e) => setStudentPrice(e.target.value)}
                    placeholder="Free"
                  />
                </Field>
              ) : null}
              {pricingParticipantTypes.includes('guest') ? (
                <Field>
                  <FieldLabel htmlFor="play-event-settings-guest-price">Guest price</FieldLabel>
                  <Input
                    id="play-event-settings-guest-price"
                    type="number"
                    min={0}
                    step="0.01"
                    value={guestPrice}
                    onChange={(e) => setGuestPrice(e.target.value)}
                    placeholder="Free"
                  />
                </Field>
              ) : null}
            </div>
          </SettingsSection>

          <SettingsSection
            title="Public site"
            description="Control how this event appears on your public club page."
            compact
          >
            <div className="space-y-6">
              <LessonCoverImageField
                inputId="play-event-settings-cover-image"
                displayUrl={coverDisplayUrl}
                uploading={uploadingCover}
                disabled={saving}
                onFileSelect={handleCoverFileSelect}
                onRemove={handleCoverRemove}
                description="Shown on your public play events page. Max 5MB, images only."
              />

              <div className="grid gap-4 md:grid-cols-2">
                <div className="flex h-full flex-col rounded-xl border border-border bg-muted/20 p-4 sm:p-5">
                  <div className="space-y-1">
                    <h4 className="text-sm font-medium text-foreground">Kiosk registration</h4>
                    <p className="text-sm text-muted-foreground">
                      Let members and students register on the day at the event kiosk. Ideal for
                      social events with last-minute drop-ins.
                    </p>
                  </div>
                  <div className="mt-4 space-y-3">
                    <label className="flex items-start gap-3 rounded-lg border border-border bg-card px-3 py-3 text-sm">
                      <Checkbox
                        className="mt-0.5"
                        checked={kioskRegistrationEnabled}
                        onCheckedChange={(value) => {
                          const enabled = Boolean(value);
                          setKioskRegistrationEnabled(enabled);
                          if (!enabled) {
                            setKioskGuestRegistrationEnabled(false);
                          }
                        }}
                      />
                      <span className="font-medium text-foreground">Allow kiosk registration</span>
                    </label>
                    {kioskRegistrationEnabled && pricingParticipantTypes.includes('guest') ? (
                      <label className="flex items-start gap-3 rounded-lg border border-border bg-card px-3 py-3 text-sm">
                        <Checkbox
                          className="mt-0.5"
                          checked={kioskGuestRegistrationEnabled}
                          onCheckedChange={(value) =>
                            setKioskGuestRegistrationEnabled(Boolean(value))
                          }
                        />
                        <span className="text-foreground">
                          <span className="font-medium">Allow guest registration at kiosk</span>
                          <span className="mt-1 block text-sm font-normal text-muted-foreground">
                            Walk-in guests can register and check in from the kiosk when this is
                            enabled.
                          </span>
                        </span>
                      </label>
                    ) : null}
                  </div>
                </div>

                <div className="flex h-full flex-col rounded-xl border border-border bg-muted/20 p-4 sm:p-5">
                  <div className="space-y-1">
                    <h4 className="text-sm font-medium text-foreground">Featured listing</h4>
                    <p className="text-sm text-muted-foreground">
                      Highlight this event on your public home page so visitors see it first.
                    </p>
                  </div>
                  <label className="mt-4 flex items-start gap-3 rounded-lg border border-border bg-card px-3 py-3 text-sm">
                    <Checkbox
                      className="mt-0.5"
                      checked={isFeatured}
                      onCheckedChange={(value) => setIsFeatured(Boolean(value))}
                    />
                    <span className="font-medium text-foreground">Feature on public home page</span>
                  </label>
                </div>
              </div>
            </div>
          </SettingsSection>

          <div className="flex flex-wrap justify-end gap-2 border-t border-border pt-4">
            <Button type="button" variant="outline" onClick={handleCancelEdit} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" form={SETTINGS_FORM_ID} isLoading={saving}>
              Save settings
            </Button>
          </div>
        </form>
      ) : (
        <div className="divide-y divide-border">
          <SettingsSection title="Format">
            <div className="flex flex-wrap gap-2">
              <Badge variant="secondary">{PLAY_EVENT_FORMAT_LABELS[event.format]}</Badge>
              <Badge variant="outline">{PLAY_EVENT_SIDE_LABELS[event.side]}</Badge>
              {event.isFeatured ? (
                <Badge variant="warning" className="gap-1">
                  <Star className="h-3 w-3 fill-current" aria-hidden />
                  Featured
                </Badge>
              ) : null}
            </div>
          </SettingsSection>

          <SettingsSection title="Schedule">
            <dl className="grid gap-4 sm:grid-cols-2">
              <SettingItem
                label="Starts"
                value={format(startsAtDate, 'EEEE d MMM yyyy · HH:mm')}
              />
              <SettingItem label="Ends" value={format(endsAtDate, 'EEEE d MMM yyyy · HH:mm')} />
            </dl>
          </SettingsSection>

          <SettingsSection title="Courts">
            <SettingItem label="Reserved courts" value={courtLabels} />
          </SettingsSection>

          <SettingsSection title="Registration">
            <dl className="grid gap-4 sm:grid-cols-2">
              <SettingItem
                label="Join policy"
                value={PLAY_JOIN_POLICY_LABELS[event.joinPolicy]}
              />
              <SettingItem
                label="Payment"
                value={PLAY_PAYMENT_MODE_LABELS[event.paymentMode]}
              />
              <SettingItem
                label="Gender restriction"
                value={PLAY_GENDER_RESTRICTION_LABELS[event.genderRestriction ?? 'open']}
              />
              <SettingItem label="Capacity" value={event.capacity ?? 'Unlimited'} />
              <SettingItem
                label="Matchup visibility"
                value={`${event.matchupVisibilityMinutes} min before start`}
              />
              <SettingItem
                label="Match scoring"
                value={event.matchScoringEnabled ? 'Enabled' : 'Disabled'}
              />
            </dl>
          </SettingsSection>

          <SettingsSection
            title="Public site"
            description="How this event appears on your public club page."
          >
            <div className="space-y-6">
              <div className="rounded-lg border border-border bg-muted/20 p-4">
                <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Cover image
                </dt>
                <dd className="mt-3">
                  {event.coverImageUrl ? (
                    <img
                      src={event.coverImageUrl}
                      alt=""
                      className="aspect-video w-full max-w-md rounded-md border border-border object-cover"
                    />
                  ) : (
                    <p className="text-sm font-medium text-foreground">None</p>
                  )}
                </dd>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-lg border border-border bg-muted/20 p-4">
                  <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Kiosk registration
                  </dt>
                  <dd className="mt-3 space-y-1 text-sm font-medium text-foreground">
                    <p>{event.kioskRegistrationEnabled ? 'Enabled' : 'Disabled'}</p>
                    {event.kioskRegistrationEnabled ? (
                      <p className="text-sm font-normal text-muted-foreground">
                        {event.kioskGuestRegistrationEnabled
                          ? 'Guests may register at the kiosk'
                          : 'Members and students only at the kiosk'}
                      </p>
                    ) : null}
                  </dd>
                </div>

                <div className="rounded-lg border border-border bg-muted/20 p-4">
                  <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Public home page
                  </dt>
                  <dd className="mt-3 text-sm font-medium text-foreground">
                    {event.isFeatured ? (
                      <span className="inline-flex items-center gap-1.5">
                        <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-500" aria-hidden />
                        Featured on home page
                      </span>
                    ) : (
                      'Not featured'
                    )}
                  </dd>
                </div>
              </div>
            </div>
          </SettingsSection>

          {event.format === 'social_rotate' ? (
            <SettingsSection title="Social rotate">
              <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <SettingItem label="Rounds" value={event.roundCount ?? '—'} />
                <SettingItem
                  label="Round duration"
                  value={
                    event.roundDurationMinutes != null
                      ? `${event.roundDurationMinutes} min`
                      : '—'
                  }
                />
                <SettingItem
                  label="Break between rounds"
                  value={
                    event.roundBreakMinutes != null ? `${event.roundBreakMinutes} min` : '—'
                  }
                />
                <SettingItem
                  label="Side"
                  value={PLAY_EVENT_SIDE_LABELS[event.side]}
                />
                <SettingItem
                  label="Odd players"
                  value={
                    event.oddPlayerPolicy
                      ? ODD_PLAYER_POLICY_LABELS[event.oddPlayerPolicy]
                      : '—'
                  }
                />
                <SettingItem
                  label="Next-match withdraw"
                  value={`${event.nextMatchWithdrawMinutes} min`}
                />
              </dl>
            </SettingsSection>
          ) : null}

          <SettingsSection title="Pricing">
            <dl
              className={cn('grid gap-4', pricingGridCols(readOnlyPricingParticipantTypes.length))}
            >
              {readOnlyPricingParticipantTypes.includes('member') ? (
                <SettingItem label="Member" value={formatPriceCents(event.memberPriceCents)} />
              ) : null}
              {readOnlyPricingParticipantTypes.includes('student') ? (
                <SettingItem label="Student" value={formatPriceCents(event.studentPriceCents)} />
              ) : null}
              {readOnlyPricingParticipantTypes.includes('guest') ? (
                <SettingItem label="Guest" value={formatPriceCents(event.guestPriceCents)} />
              ) : null}
            </dl>
          </SettingsSection>

          {!canEdit ? (
            <div className="px-4 py-4 sm:px-6">
              <p className="text-sm text-muted-foreground">
                Settings cannot be edited after the event is completed or cancelled.
              </p>
            </div>
          ) : null}
        </div>
      )}
    </DashboardDataCard>
  );
}
